import { useState, useEffect, useCallback } from 'react';
import { DealerManager } from '@/utils/dealerManager';
import { 
  fetchCertificationStyles, 
  clearStylesCache, 
  DEFAULT_STYLES,
  type CertificationStyles 
} from '@/services/CertificationStylesService';

export const useCertificationStyles = () => {
  const [styles, setStyles] = useState<CertificationStyles>(DEFAULT_STYLES);
  const [isLoading, setIsLoading] = useState(true);

  const loadStyles = useCallback(async () => {
    if (!DealerManager.getCurrentDealer()) {
      return; // Wait for dealer config
    }

    try {
      setIsLoading(true);
      const styles = await fetchCertificationStyles();
      setStyles(styles);
    } catch (error) {
      console.error('Error loading certification styles:', error);
      setStyles(DEFAULT_STYLES);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Force clear cache on mount to ensure fresh data
    clearStylesCache();
    
    // Check immediately if dealer is already available
    const dealerConfig = DealerManager.getCurrentDealer();
    if (dealerConfig) {
      loadStyles();
      return;
    }

    // If no dealer yet, check a few times with exponential backoff
    let attempts = 0;
    const maxAttempts = 5; // Reduced attempts
    let checkTimeout: NodeJS.Timeout;
    
    const checkForDealer = () => {
      const dealerConfig = DealerManager.getCurrentDealer();
      attempts++;
      
      if (dealerConfig) {
        loadStyles();
      } else if (attempts < maxAttempts) {
        // Exponential backoff: 100ms, 300ms, 700ms, 1500ms
        const delay = 100 * Math.pow(2, attempts) - 100;
        checkTimeout = setTimeout(checkForDealer, delay);
      } else {
        setIsLoading(false);
      }
    };

    // Start checking after a small delay
    checkTimeout = setTimeout(checkForDealer, 100);

    // Listen for dealer change events
    const handleDealerChange = () => {
      clearStylesCache();
      loadStyles();
    };

    window.addEventListener('dealerChanged', handleDealerChange);

    return () => {
      clearTimeout(checkTimeout);
      window.removeEventListener('dealerChanged', handleDealerChange);
    };
  }, [loadStyles]);

  return { styles, isLoading };
};
